import { useCallback, useEffect, useState } from "react";
import type { EventsResponse } from "../../shared/types.js";
import { CALENDARS } from "../../shared/calendars.js";
import { groupByDay, relativeDayLabel, todayHawaiiKey, shiftDayKey } from "../lib/eventTime.js";
import { EventCard } from "../components/EventCard.js";
import { navigate } from "../router.js";
import { BRANDING } from "../../shared/branding.js";

type FilterKind = "all" | "primary" | "secondary" | "sister";

function matchesFilter(sourceId: string, f: FilterKind): boolean {
  if (f === "all") return true;
  if (f === "primary") return BRANDING.calendarPrimary.sourceIds.includes(sourceId);
  if (f === "secondary") return BRANDING.calendarSecondary.sourceIds.includes(sourceId);
  if (f === "sister") {
    return (
      !BRANDING.calendarPrimary.sourceIds.includes(sourceId) &&
      !BRANDING.calendarSecondary.sourceIds.includes(sourceId)
    );
  }
  return false;
}

const FUTURE_WINDOW_DAYS = 15;

/** Browsers that support PWA install programmatically fire beforeinstallprompt
 *  (Chrome / Edge / Brave on most platforms). iOS Safari does not — we fall
 *  back to scrolling to the manual install instructions. */
type InstallPromptEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS-specific
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

export function Calendar() {
  const [data, setData] = useState<EventsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterKind>("all");
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(() => isStandalone());

  useEffect(() => {
    function onBeforeInstall(e: Event) {
      e.preventDefault();
      setInstallPrompt(e as InstallPromptEvent);
    }
    function onInstalled() {
      setInstallPrompt(null);
      setInstalled(true);
    }
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (installPrompt) {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      setInstallPrompt(null);
      if (choice.outcome === "accepted") setInstalled(true);
    } else {
      // Browser doesn't support programmatic install (iOS Safari, etc.) —
      // jump to the inline install instructions further down the page.
      document.getElementById("install-app")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [installPrompt]);

  useEffect(() => {
    let cancelled = false;
    // No `from` — uses server default (start of Hawaii today), so past events
    // are filtered out at the API level. To see past hashes, users go to the
    // month grid view at /calendar/month.
    fetch("/api/events")
      .then((r) => r.json())
      .then((d: EventsResponse) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const subscribeUrl = `webcal://${window.location.host}/api/feed.ics`;
  const downloadUrl = "/api/feed.ics";
  const googleCalUrl = `https://calendar.google.com/calendar/u/0/r?cid=${encodeURIComponent(
    `https://${window.location.host}/api/feed.ics`,
  )}`;

  if (error) {
    return (
      <section className="page-calendar">
        <h1>Calendar</h1>
        <p className="error-banner">Couldn't load events: {error}</p>
      </section>
    );
  }

  if (!data) {
    return (
      <section className="page-calendar">
        <h1>Calendar</h1>
        <p className="muted">Loading…</p>
      </section>
    );
  }

  const filtered = data.events.filter((e) => matchesFilter(e.sourceId, filter));
  const today = todayHawaiiKey();
  const futureCutoff = shiftDayKey(today, FUTURE_WINDOW_DAYS);
  // Server default already trims to today onward; we cap on the client at
  // today + 15 days. Anything older or further out lives in the month grid.
  const groups = groupByDay(filtered).filter((g) => g.dateKey <= futureCutoff);
  const failedSources = data.sources.filter((s) => !s.ok);

  const counts = {
    all: data.events.length,
    primary: data.events.filter((e) => matchesFilter(e.sourceId, "primary")).length,
    secondary: data.events.filter((e) => matchesFilter(e.sourceId, "secondary")).length,
    sister: data.events.filter((e) => matchesFilter(e.sourceId, "sister")).length,
  };

  return (
    <section className="page-calendar">
      <header className="calendar-head">
        <h1>Trails &amp; Events</h1>
        <p className="muted">
          {BRANDING.calendarPrimary.label} events plus what {BRANDING.calendarSecondary.label} and
          our sister kennels are running. Tap an event for details.
        </p>
        <div className="calendar-subscribe">
          {!installed && (
            <button type="button" className="btn btn-primary" onClick={handleInstall}>
              Add to Home Screen
            </button>
          )}
          <div className="calendar-add-row">
            <a href={subscribeUrl} className="btn btn-primary btn-sm">
              Add to Calendar
            </a>
            <a
              href={googleCalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary btn-sm"
            >
              Add to Google Calendar
            </a>
            <a href={downloadUrl} className="btn btn-secondary btn-sm">
              Download .ics
            </a>
          </div>
        </div>
        <p className="callout">
          Tap "Add to Home Screen" to install this site as an app on your phone or desktop —
          dedicated icon, opens like a native app. "Add to Calendar" puts the {BRANDING.appShortName}{" "}
          schedule in your device's default calendar app (Apple, Outlook, etc.); "Add to Google
          Calendar" does the same for Google users; or grab the .ics for any other calendar app.
          On On! 😉
        </p>
      </header>

      <div className="calendar-filters" role="tablist" aria-label="Filter events">
        <FilterTab label={`All (${counts.all})`} active={filter === "all"} onClick={() => setFilter("all")} />
        <FilterTab
          label={`${BRANDING.calendarPrimary.label} (${counts.primary})`}
          active={filter === "primary"}
          onClick={() => setFilter("primary")}
        />
        <FilterTab
          label={`${BRANDING.calendarSecondary.label} (${counts.secondary})`}
          active={filter === "secondary"}
          onClick={() => setFilter("secondary")}
        />
        <FilterTab
          label={`Sister kennels (${counts.sister})`}
          active={filter === "sister"}
          onClick={() => setFilter("sister")}
        />
      </div>

      <div className="event-list-pane" id="event-list-pane">
        {groups.length === 0 ? (
          <p className="muted empty-state">
            No upcoming events. The kennels may not have anything scheduled yet — check back soon.
          </p>
        ) : (
          <ol className="event-day-list">
            {groups.map((g) => (
              <li key={g.dateKey} className="event-day-group">
                <h2 className="event-day-heading">
                  {relativeDayLabel(g.dateKey) && (
                    <span className="event-day-relative">{relativeDayLabel(g.dateKey)}</span>
                  )}
                  {g.label}
                </h2>
                <div className="event-day-events">
                  {g.events.map((ev) => (
                    <EventCard key={ev.id} event={ev} />
                  ))}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      <div className="calendar-more">
        <a
          href="/calendar/month"
          className="btn btn-secondary"
          onClick={(e) => {
            e.preventDefault();
            navigate("/calendar/month");
          }}
        >
          View full calendar (month grid) →
        </a>
        <p className="muted calendar-more-note">
          Cards above cover today and the next 2 weeks. For past hashes or anything further out,
          switch to the month grid.
        </p>
      </div>

      <section id="install-app" className="install-app">
        <h2>Install as a home-screen app</h2>
        <p>
          Save this site to your home screen and it opens like any other app — no browser chrome,
          dedicated icon, fast.
        </p>

        <h3>iPhone / iPad (Safari)</h3>
        <ol>
          <li>Tap the Share button (square with ↑) at the bottom of the screen.</li>
          <li>Scroll down and tap <strong>Add to Home Screen</strong>.</li>
          <li>Tap <strong>Add</strong> in the top right.</li>
        </ol>

        <h3>Android (Chrome)</h3>
        <ol>
          <li>Tap the <strong>⋮</strong> menu in the top right.</li>
          <li>Tap <strong>Install app</strong> (older Android: "Add to Home screen").</li>
          <li>Confirm.</li>
        </ol>

        <h3>Desktop (Chrome / Edge)</h3>
        <ol>
          <li>Look for the install icon at the right of the URL bar (a small computer with an arrow, or a ⊕).</li>
          <li>Click it, then click <strong>Install</strong>.</li>
        </ol>

        <p className="muted">
          More detailed walkthroughs:{" "}
          <a href="https://web.dev/learn/pwa/installation" target="_blank" rel="noopener noreferrer">
            web.dev's PWA installation guide
          </a>
          .
        </p>
      </section>

      <footer className="calendar-foot muted">
        <p>
          Sourced from {CALENDARS.length} calendars · last refreshed{" "}
          {new Date(data.fetchedAt).toLocaleString()}
        </p>
        {failedSources.length > 0 && (
          <p className="warn">
            Couldn't fetch: {failedSources.map((s) => s.name).join(", ")}
          </p>
        )}
      </footer>
    </section>
  );
}

function FilterTab({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={`calendar-filter${active ? " is-active" : ""}`}
      onClick={onClick}
    >
      {label}
    </button>
  );
}
